import React from 'react';
import { Box, Card, Chip, Typography } from '@mui/material';
import { format, subDays } from 'date-fns';
import {
	CheckCircleOutline,
	LocalOffer,
	LocalShipping,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useTicketTypeMeta } from 'containers/pages/CANpark/context/TicketTerminologyProvider';
import { TicketType } from 'containers/pages/CANpark/types/ticket';

export const ViolationPreview: React.FC = () => {
	const { watch } = useFormContext<ParkingLocation>();
	const getTicketTypeMeta = useTicketTypeMeta();

	const {
		violationCheckType,
		violationCheckWindowDays = 7,
		maxStayMinutes = 0,
		taggingPolicyType,
		taggingAfterViolations = 1,
		towPolicyType,
		towAfterViolations = 3,
	} = watch();

	const length =
		towPolicyType === 'afterXViolations'
			? towAfterViolations + 1
			: taggingAfterViolations * 2;

	const hoursPart = Math.floor(maxStayMinutes / 60);
	const minutesPart = maxStayMinutes % 60;

	return (
		<Card sx={{ padding: 1, mt: 2 }}>
			<Typography variant="body2" fontWeight={300}>
				Violation Rules Preview
			</Typography>

			{violationCheckType === 'fixedMonthly' && (
				<Typography variant="caption" fontStyle="italic">
					The system will only include violations from the start of
					the month. For example, since it's{' '}
					{format(new Date(), 'do MMMM yyyy')}, the system will check
					from 1st to today.
				</Typography>
			)}

			{violationCheckType === 'rolling' && (
				<Typography variant="caption" fontStyle="italic">
					Since today is {format(new Date(), 'do MMMM yyyy')} and
					you're checking the last {violationCheckWindowDays} days,
					the system will look from{' '}
					{format(
						subDays(new Date(), violationCheckWindowDays - 1),
						'do MMMM yyyy',
					)}{' '}
					to today.
				</Typography>
			)}

			{violationCheckType === 'hourlyExpiry' && (
				<Box display="flex" flexDirection="column" gap={0.5} mt={1}>
					<Typography variant="caption" fontStyle="italic">
						Vehicles may stay up to
						{hoursPart ? ` ${hoursPart}h` : ''}
						{minutesPart ? ` ${minutesPart}m` : ''}
						{!maxStayMinutes && ' (not set)'}. A re-scan after the
						limit is flagged as a warning; if the tow policy is set,
						the {towAfterViolations}th overstay in the last 30 days
						escalates to a tow.
					</Typography>
					<Typography variant="caption" fontStyle="italic">
						Warning policy is not used in this mode.
					</Typography>
				</Box>
			)}

			{violationCheckType !== 'hourlyExpiry' && (
			<Box display="flex" flexDirection="column" mt={1} gap={1}>
				{Array.from(
					{
						length,
					},
					(_, index) => {
						const num = index + 1;
						let ticketType: TicketType = 'log';
						let icon = <CheckCircleOutline fontSize="small" />;
						let color: 'default' | 'warning' | 'error' = 'default';

						if (
							taggingPolicyType === 'afterXViolations' &&
							num % taggingAfterViolations === 0
						) {
							ticketType = 'logAndWarn';
							icon = <LocalOffer fontSize="small" />;
							color = 'warning';
						}

						if (
							towPolicyType === 'afterXViolations' &&
							num > towAfterViolations
						) {
							ticketType = 'tow';
							icon = <LocalShipping fontSize="small" />;
							color = 'error';
						}

						const { shortLabel } = getTicketTypeMeta(ticketType);

						return (
							<Box
								key={num}
								display="flex"
								alignItems="center"
								gap={1}
								ml={2}
							>
								<Typography variant="body2" fontWeight={300}>
									Violation {num}
								</Typography>
								<Chip
									icon={icon}
									label={shortLabel}
									color={color}
									variant="outlined"
									size="small"
								/>
							</Box>
						);
					},
				)}
			</Box>
			)}
		</Card>
	);
};
